/**
 * Utility functions for extracting dominant colors from images
 * Similar to YouTube Studio's thumbnail background color approach
 */

interface RGB {
  r: number;
  g: number;
  b: number;
}

interface ColorPalette {
  dominant: string;
  secondary: string;
  accent: string;
  background: string;
}

/**
 * Extract dominant colors from an image
 * @param imageSrc - The image source URL
 * @returns Promise<ColorPalette> - Object containing color palette
 */
export const extractImageColors = async (
  imageSrc: string
): Promise<ColorPalette> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        // Set canvas size to a smaller size for performance
        const maxSize = 150;
        const ratio = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        // Draw image to canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        // Sample pixels and extract colors
        const colors: RGB[] = [];
        const sampleRate = 10; // Sample every 10th pixel for performance

        for (let i = 0; i < pixels.length; i += sampleRate * 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];

          // Skip transparent pixels
          if (a > 128) {
            colors.push({ r, g, b });
          }
        }

        // Calculate dominant colors
        const palette = calculateColorPalette(colors);
        resolve(palette);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = imageSrc;
  });
};

/**
 * Calculate color palette from RGB values
 * @param colors - Array of RGB color objects
 * @returns ColorPalette - Calculated color palette
 */
const calculateColorPalette = (colors: RGB[]): ColorPalette => {
  if (colors.length === 0) {
    return getDefaultPalette();
  }

  // Group similar colors
  const colorGroups = groupSimilarColors(colors);

  // Sort by frequency
  const sortedGroups = colorGroups.sort((a, b) => b.count - a.count);

  // Find the darkest color from all colors
  const darkestColor = findDarkestColor(colors);

  // Extract dominant colors
  const dominant = sortedGroups[0]?.color || { r: 0, g: 0, b: 0 };
  const secondary = sortedGroups[1]?.color || dominant;
  const accent = sortedGroups[2]?.color || secondary;

  // Use the darkest color as the primary color for striking backgrounds
  const vibrantDominant = darkestColor;
  const vibrantSecondary = {
    r: Math.round((darkestColor.r + dominant.r) / 2),
    g: Math.round((darkestColor.g + dominant.g) / 2),
    b: Math.round((darkestColor.b + dominant.b) / 2),
  };

  // Create background color (lighter version of darkest for contrast)
  const background = lightenColor(darkestColor, 0.7);

  return {
    dominant: rgbToHex(vibrantDominant),
    secondary: rgbToHex(vibrantSecondary),
    accent: rgbToHex(accent),
    background: rgbToHex(background),
  };
};

/**
 * Find the darkest color from an array of RGB colors
 * @param colors - Array of RGB colors
 * @returns RGB - The darkest color
 */
const findDarkestColor = (colors: RGB[]): RGB => {
  if (colors.length === 0) {
    return { r: 0, g: 0, b: 0 };
  }

  let darkest = colors[0];
  let minBrightness = 255;

  for (const color of colors) {
    // Calculate brightness using luminance formula
    const brightness = color.r * 0.299 + color.g * 0.587 + color.b * 0.114;

    if (brightness < minBrightness) {
      minBrightness = brightness;
      darkest = color;
    }
  }

  return darkest;
};

/**
 * Group similar colors together
 * @param colors - Array of RGB colors
 * @returns Array of grouped colors with counts
 */
const groupSimilarColors = (
  colors: RGB[]
): Array<{ color: RGB; count: number }> => {
  const groups: Array<{ color: RGB; count: number }> = [];
  const threshold = 30; // Color similarity threshold

  for (const color of colors) {
    let found = false;

    for (const group of groups) {
      if (colorDistance(color, group.color) < threshold) {
        group.count++;
        group.color = {
          r: Math.round((group.color.r + color.r) / 2),
          g: Math.round((group.color.g + color.g) / 2),
          b: Math.round((group.color.b + color.b) / 2),
        };
        found = true;
        break;
      }
    }

    if (!found) {
      groups.push({ color: { ...color }, count: 1 });
    }
  }

  return groups;
};

/**
 * Calculate color distance between two RGB colors
 * @param color1 - First RGB color
 * @param color2 - Second RGB color
 * @returns Distance value
 */
const colorDistance = (color1: RGB, color2: RGB): number => {
  const dr = color1.r - color2.r;
  const dg = color1.g - color2.g;
  const db = color1.b - color2.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
};

/**
 * Lighten a color by a factor
 * @param color - RGB color to lighten
 * @param factor - Lightening factor (0-1)
 * @returns Lightened RGB color
 */
const lightenColor = (color: RGB, factor: number): RGB => {
  return {
    r: Math.round(color.r + (255 - color.r) * factor),
    g: Math.round(color.g + (255 - color.g) * factor),
    b: Math.round(color.b + (255 - color.b) * factor),
  };
};

/**
 * Convert RGB to hex color
 * @param rgb - RGB color object
 * @returns Hex color string
 */
const rgbToHex = (rgb: RGB): string => {
  return `#${((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b)
    .toString(16)
    .slice(1)}`;
};

/**
 * Get default color palette when image processing fails
 * @returns Default ColorPalette
 */
const getDefaultPalette = (): ColorPalette => {
  return {
    dominant: "#1e40af",
    secondary: "#6d28d9",
    accent: "#be185d",
    background: "#dbeafe",
  };
};

/**
 * Generate CSS gradient from color palette
 * @param palette - Color palette
 * @returns CSS gradient string
 */
export const generateGradient = (palette: ColorPalette): string => {
  // Convert hex to RGB for manipulation
  const hexToRgb = (hex: string): RGB => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };
  };

  const rgbToHex = (rgb: RGB): string => {
    return `#${((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b)
      .toString(16)
      .slice(1)}`;
  };

  const dominantRgb = hexToRgb(palette.dominant);
  const secondaryRgb = hexToRgb(palette.secondary);
  const DOMINANT_LIGHTEN = 0.15;
  const SECONDARY_LIGHTEN = 0.15;
  const BASE_LIGHTEN = 0.45;

  const brightDominant = {
    r: Math.min(
      255,
      Math.round(dominantRgb.r + (255 - dominantRgb.r) * DOMINANT_LIGHTEN)
    ),
    g: Math.min(
      255,
      Math.round(dominantRgb.g + (255 - dominantRgb.g) * DOMINANT_LIGHTEN)
    ),
    b: Math.min(
      255,
      Math.round(dominantRgb.b + (255 - dominantRgb.b) * DOMINANT_LIGHTEN)
    ),
  };

  const brightSecondary = {
    r: Math.min(
      255,
      Math.round(secondaryRgb.r + (255 - secondaryRgb.r) * SECONDARY_LIGHTEN)
    ),
    g: Math.min(
      255,
      Math.round(secondaryRgb.g + (255 - secondaryRgb.g) * SECONDARY_LIGHTEN)
    ),
    b: Math.min(
      255,
      Math.round(secondaryRgb.b + (255 - secondaryRgb.b) * SECONDARY_LIGHTEN)
    ),
  };

  // Create darker base color (less lightening for darker background)
  const lightBase = {
    r: Math.round(brightDominant.r + (255 - brightDominant.r) * BASE_LIGHTEN),
    g: Math.round(brightDominant.g + (255 - brightDominant.g) * BASE_LIGHTEN),
    b: Math.round(brightDominant.b + (255 - brightDominant.b) * BASE_LIGHTEN),
  };

  const brightDominantHex = rgbToHex(brightDominant);
  const brightSecondaryHex = rgbToHex(brightSecondary);
  const lightBaseHex = rgbToHex(lightBase);

  return `linear-gradient(135deg, ${lightBaseHex} 0%, ${brightDominantHex}90 30%, ${brightSecondaryHex}80 70%, ${lightBaseHex} 100%)`;
};

/**
 * Generate subtle background color from palette
 * @param palette - Color palette
 * @returns CSS color string
 */
export const generateBackgroundColor = (palette: ColorPalette): string => {
  // Convert hex to RGB for manipulation
  const hexToRgb = (hex: string): RGB => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };
  };

  const dominantRgb = hexToRgb(palette.dominant);

  // Create a darker, more saturated version of the dominant color
  const lightened = {
    r: Math.round(dominantRgb.r + (255 - dominantRgb.r) * 0.7),
    g: Math.round(dominantRgb.g + (255 - dominantRgb.g) * 0.7),
    b: Math.round(dominantRgb.b + (255 - dominantRgb.b) * 0.7),
  };

  // Add a slight desaturation for a more subtle look
  const gray = Math.round(
    lightened.r * 0.3 + lightened.g * 0.59 + lightened.b * 0.11
  );
  const desaturated = {
    r: Math.round(lightened.r * 0.7 + gray * 0.3),
    g: Math.round(lightened.g * 0.7 + gray * 0.3),
    b: Math.round(lightened.b * 0.7 + gray * 0.3),
  };

  return rgbToHex(desaturated);
};
